import { fromUtf8, positiveInteger } from '@viko/crypto'
import { EncryptedContent, type EncryptionContext, TranscriptEvent } from '@viko/protocol'
import type { ContentVault } from './content-vault.js'

/** Authenticate and validate the entire batch before giving anything to TranscriptStore/UI. */
export async function decryptTranscriptBatch(
  vault: ContentVault,
  accountId: string,
  runId: string,
  input: readonly unknown[],
  minimumEpoch: number,
): Promise<TranscriptEvent[]> {
  positiveInteger(minimumEpoch)
  const objects = input.map((raw) => EncryptedContent.parse(raw))
  for (const object of objects) {
    const c = object.context
    if (c.accountId !== accountId || c.runId !== runId || c.epoch < minimumEpoch)
      throw new Error('wrong transcript context')
  }
  // j-privacy: within a run, a later event never uses an older epoch (a retired key's forgery).
  const bySeq = [...objects].sort((a, b) => a.context.seq - b.context.seq)
  for (let i = 1; i < bySeq.length; i++)
    if (bySeq[i]!.context.epoch < bySeq[i - 1]!.context.epoch)
      throw new Error('transcript epoch downgrade')
  const bytes = await vault.openBatch(accountId, objects)
  return objects.map((object, i) => {
    const plain = bytes[i]
    if (!plain) throw new Error('missing decrypted event')
    const event = TranscriptEvent.parse(JSON.parse(fromUtf8(plain)))
    const c = object.context
    if (
      event.runId !== runId ||
      event.eventId !== c.objectId ||
      event.seq !== c.seq ||
      event.kind !== c.kind
    )
      throw new Error('transcript envelope mismatch')
    return event
  })
}

/** Fetch exact immutable coordinates and authenticate before emitting any decrypted events. */
export async function fetchTranscriptBatch(
  api: { getContent(context: EncryptionContext): Promise<EncryptedContent> },
  vault: ContentVault,
  accountId: string,
  runId: string,
  contexts: readonly EncryptionContext[],
  minimumEpoch: number,
): Promise<TranscriptEvent[]> {
  positiveInteger(minimumEpoch)
  if (contexts.length > 64) throw new Error('content batch too large')
  // Snapshot trusted coordinates before any asynchronous fetch.
  const expected = structuredClone(contexts)
  for (const c of expected) {
    if (c.accountId !== accountId || c.runId !== runId || c.epoch < minimumEpoch)
      throw new Error('wrong transcript context')
  }
  const objects: EncryptedContent[] = []
  for (const c of expected) {
    const content = await api.getContent(c)
    // Use request context as AEAD input, never a relay-supplied placement.
    objects.push({ context: c, box: content.box })
  }
  return decryptTranscriptBatch(vault, accountId, runId, objects, minimumEpoch)
}
